import { db } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import { generateRoomUrl } from '../utils/roomUtils';

// This would be from environment variables in a real implementation
const TWILIO_ACCOUNT_SID = 'AC60ff4655010e220c188aa57eca70964b';
const TWILIO_AUTH_TOKEN = 'd70648487e66f2ba9998c435e938c07c';

interface VirtualEventSession {
   id?: string;
   eventId: string;
   roomName: string;
   roomSid?: string;
   roomUrl?: string;
   status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
   startTime?: Date;
   endTime?: Date;
   recordingEnabled: boolean;
   recordingSid?: string;
   recordingUrl?: string;
   participantCount?: number;
   createdAt: Date;
   updatedAt: Date;
}

interface TwilioRoomResponse {
   roomSid: string;
   roomName: string;
   status: string;
   dateCreated: string;
   url: string;
   maxParticipants: number;
   existing: boolean;
}

/**
 * Creates a new Twilio video room for a virtual event
 */
export const createVirtualEventRoom = async (
   eventId: string,
   roomName: string,
   enableRecording: boolean
): Promise<VirtualEventSession & { joinUrl?: string }> => {
   try {
      // Make the API call to create the Twilio room
      const { getAuthHeaders } = await import('../utils/auth');
      const headers = await getAuthHeaders();
      
      const response = await axios.post(
         `${process.env.REACT_APP_API_URL}/api/twilio/create-room`,
         {
            roomName,
            roomType: 'group',
            maxParticipants: 10,
            recordParticipantsOnConnect: enableRecording,
         },
         { headers }
      );

      const twilioRoom: TwilioRoomResponse = response.data;

      // Generate a joinable URL for participants
      const joinUrl = generateRoomUrl({
         roomSid: twilioRoom.roomSid,
         roomName: twilioRoom.roomName,
         eventId: eventId,
         eventTitle: roomName,
      });

      // Create a session record in Firestore
      const session: VirtualEventSession = {
         eventId,
         roomName: twilioRoom.roomName,
         roomSid: twilioRoom.roomSid,
         roomUrl: twilioRoom.url,
         status: 'scheduled',
         recordingEnabled: enableRecording,
         createdAt: new Date(),
         updatedAt: new Date(),
      };

      const sessionRef = await addDoc(collection(db, 'virtualEventSessions'), session);

      return {
         ...session,
         id: sessionRef.id,
         joinUrl: joinUrl,
      };
   } catch (error) {
      console.error('Error creating virtual event room:', error);
      // Fallback to mock data if API fails
      const mockRoomSid = `RM${Math.random().toString(36).substr(2, 9)}`;

      // Generate mock join URL
      const joinUrl = generateRoomUrl({
         roomSid: mockRoomSid,
         roomName: roomName,
         eventId: eventId,
         eventTitle: roomName,
      });

      const session: VirtualEventSession = {
         eventId,
         roomName,
         roomSid: mockRoomSid,
         roomUrl: `https://twilio.com/room/${mockRoomSid}`,
         status: 'scheduled',
         recordingEnabled: enableRecording,
         createdAt: new Date(),
         updatedAt: new Date(),
      };

      const sessionRef = await addDoc(collection(db, 'virtualEventSessions'), session);

      return {
         ...session,
         id: sessionRef.id,
         joinUrl: joinUrl,
      };
   }
};

/**
 * Starts a scheduled virtual event session
 */
export const startVirtualEventSession = async (sessionId: string): Promise<VirtualEventSession> => {
   try {
      const sessionRef = doc(db, 'virtualEventSessions', sessionId);
      const sessionSnap = await getDoc(sessionRef);

      if (!sessionSnap.exists()) {
         throw new Error('Virtual event session not found');
      }

      const session = sessionSnap.data() as VirtualEventSession;

      // In a real implementation, this would update the Twilio room status via API

      // Update the session in Firestore
      const updatedSession = {
         ...session,
         status: 'in-progress' as const,
         startTime: new Date(),
         updatedAt: new Date(),
      };

      await updateDoc(sessionRef, updatedSession);

      return {
         ...updatedSession,
         id: sessionId,
      };
   } catch (error) {
      console.error('Error starting virtual event session:', error);
      throw new Error('Failed to start virtual event session');
   }
};

/**
 * Ends an in-progress virtual event session
 */
export const endVirtualEventSession = async (sessionId: string): Promise<VirtualEventSession> => {
   try {
      const sessionRef = doc(db, 'virtualEventSessions', sessionId);
      const sessionSnap = await getDoc(sessionRef);

      if (!sessionSnap.exists()) {
         throw new Error('Virtual event session not found');
      }

      const session = sessionSnap.data() as VirtualEventSession;

      if (session.status !== 'in-progress') {
         throw new Error('Cannot end a session that is not in progress');
      }

      // In a real implementation, this would end the Twilio room via API

      // If recording was enabled, generate a mock recording URL
      let recordingUrl;
      if (session.recordingEnabled) {
         recordingUrl = `https://api.twilio.com/recordings/${Math.random().toString(36).substr(2, 9)}`;
      }

      // Update the session in Firestore
      const updatedSession = {
         ...session,
         status: 'completed' as const,
         endTime: new Date(),
         recordingUrl,
         updatedAt: new Date(),
      };

      await updateDoc(sessionRef, updatedSession);

      return {
         ...updatedSession,
         id: sessionId,
      };
   } catch (error) {
      console.error('Error ending virtual event session:', error);
      throw new Error('Failed to end virtual event session');
   }
};

/**
 * Get active session for an event if one exists
 */
export const getActiveSessionForEvent = async (
   eventId: string
): Promise<VirtualEventSession | null> => {
   try {
      const q = query(
         collection(db, 'virtualEventSessions'),
         where('eventId', '==', eventId),
         where('status', 'in', ['scheduled', 'in-progress'])
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
         return null;
      }

      // Return the first active session
      const sessionDoc = querySnapshot.docs[0];
      return {
         ...(sessionDoc.data() as VirtualEventSession),
         id: sessionDoc.id,
      };
   } catch (error) {
      console.error('Error getting active session for event:', error);
      throw new Error('Failed to get active session');
   }
};

/**
 * Get all sessions for an event
 */
export const getSessionsForEvent = async (eventId: string): Promise<VirtualEventSession[]> => {
   try {
      const q = query(collection(db, 'virtualEventSessions'), where('eventId', '==', eventId));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
         ...(doc.data() as VirtualEventSession),
         id: doc.id,
      }));
   } catch (error) {
      console.error('Error getting sessions for event:', error);
      throw new Error('Failed to get event sessions');
   }
};

/**
 * Get session recordings for an event
 */
export const getRecordingsForEvent = async (eventId: string): Promise<VirtualEventSession[]> => {
   try {
      const q = query(
         collection(db, 'virtualEventSessions'),
         where('eventId', '==', eventId),
         where('status', '==', 'completed'),
         where('recordingUrl', '!=', null)
      );

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => ({
         ...(doc.data() as VirtualEventSession),
         id: doc.id,
      }));
   } catch (error) {
      console.error('Error getting recordings for event:', error);
      throw new Error('Failed to get event recordings');
   }
};

/**
 * Get the join URL for a virtual event session
 */
export const getSessionJoinUrl = async (sessionId: string): Promise<string | null> => {
   try {
      const sessionRef = doc(db, 'virtualEventSessions', sessionId);
      const sessionSnap = await getDoc(sessionRef);

      if (!sessionSnap.exists()) {
         return null;
      }

      const session = sessionSnap.data() as VirtualEventSession;

      if (!session.roomSid) {
         return null;
      }

      // Generate the join URL
      return generateRoomUrl({
         roomSid: session.roomSid,
         roomName: session.roomName,
         eventId: session.eventId,
         eventTitle: session.roomName,
      });
   } catch (error) {
      console.error('Error getting session join URL:', error);
      return null;
   }
};

/**
 * Get active session with join URL for an event
 */
export const getActiveSessionWithJoinUrl = async (
   eventId: string
): Promise<(VirtualEventSession & { joinUrl?: string }) | null> => {
   try {
      const session = await getActiveSessionForEvent(eventId);
      if (!session || !session.roomSid) {
         return session;
      }

      const joinUrl = generateRoomUrl({
         roomSid: session.roomSid,
         roomName: session.roomName,
         eventId: session.eventId,
         eventTitle: session.roomName,
      });

      return {
         ...session,
         joinUrl,
      };
   } catch (error) {
      console.error('Error getting active session with join URL:', error);
      return null;
   }
};

export default {
   createVirtualEventRoom,
   startVirtualEventSession,
   endVirtualEventSession,
   getActiveSessionForEvent,
   getSessionsForEvent,
   getRecordingsForEvent,
   getSessionJoinUrl,
   getActiveSessionWithJoinUrl,
}; 